import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import  TopicBox  from '../components/TopicBox.tsx';
import { Box, Typography, Link, List, ListItem, ListItemText, Divider } from '@mui/material';
import { Fade } from '@mui/material'; // Animációhoz

const FreeWebSites: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isAuthenticated = !!Cookies.get('token'); // Ellenőrzi, hogy van-e token a cookie-ban
  const [hoveredTitle, setHoveredTitle] = useState<string>('');

  useEffect(() => {
    // Simuláljuk a betöltési időt
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // 2 másodperces késleltetés

    return () => clearTimeout(timer);
  }, []);

  // Lista elemek (linkek) és témák
  const topics = [
    {
      category: 'Web Development & Coding',
      emoji: '💻',
      description: 'Tools to learn coding and build projects using modern web technologies.',
      links: [
        { 
          name: 'Codeacademy.com', 
          url: 'https://www.codecademy.com', 
          emoji: '👨‍💻', 
          tooltip: 'Interactive platform offering coding courses and hands-on lessons.' 
        },
        { 
          name: 'Codepen.io', 
          url: 'https://codepen.io', 
          emoji: '🔨', 
          tooltip: 'A social development environment for front-end designers and developers.' 
        },
        { 
          name: 'Jsfiddle.net', 
          url: 'https://jsfiddle.net', 
          emoji: '⚙️', 
          tooltip: 'Online playground for testing HTML, CSS, and JavaScript snippets.' 
        },
        { 
          name: 'Replit.com', 
          url: 'https://replit.com', 
          emoji: '🖥️', 
          tooltip: 'A collaborative online coding environment for building and deploying applications.' 
        },
      ],
    },
    {
      category: 'Design Tools',
      emoji: '🎨',
      description: 'Creative tools for designing stunning visuals and improving UI/UX.',
      links: [
        { 
          name: 'Canva.com', 
          url: 'https://www.canva.com', 
          emoji: '🖌️', 
          tooltip: 'Online design tool for creating presentations, graphics, and social media posts.' 
        },
        { 
          name: 'Figma.com', 
          url: 'https://www.figma.com', 
          emoji: '📐', 
          tooltip: 'Collaborative interface design and prototyping tool for teams.' 
        },
        { 
          name: 'Gravitdesigner.io', 
          url: 'https://www.designer.io', 
          emoji: '🖼️', 
          tooltip: 'Vector design application for creating illustrations and graphics.' 
        },
        { 
          name: 'Dungeonscrawl', 
          url: 'https://dungeonscrawl.com', 
          emoji: '🎲', 
          tooltip: 'A creative platform blending gaming elements with design challenges.' 
        },
        { 
          name: 'Pure Code AI', 
          url: 'https://purecode.ai/generations', 
          emoji: '🖌️+🤖', 
          tooltip: 'Leverages AI to generate code snippets and design ideas.' 
        },
      ],
    },
    {
      category: 'Productivity & Tools',
      emoji: '⏰',
      description: 'Enhance your workflow with tools to boost focus and productivity.',
      links: [
        { 
          name: 'Autodraw.com', 
          url: 'https://www.autodraw.com', 
          emoji: '✍️', 
          tooltip: 'AI-assisted drawing tool that turns rough sketches into refined artwork.' 
        },
        { 
          name: 'Noisli.com', 
          url: 'https://www.noisli.com', 
          emoji: '🔊', 
          tooltip: 'Ambient sound generator to improve focus and relaxation.' 
        },
        { 
          name: 'Screenshot.guru', 
          url: 'https://screenshot.guru', 
          emoji: '📸', 
          tooltip: 'Capture and share screenshots quickly and easily.' 
        },
        { 
          name: 'Fast.com', 
          url: 'https://fast.com', 
          emoji: '⚡', 
          tooltip: 'Simple internet speed test powered by Netflix.' 
        },
        { 
          name: 'Dictation.io', 
          url: 'https://dictation.io', 
          emoji: '🎤', 
          tooltip: 'Convert speech to text using advanced dictation technology.' 
        },
        { 
          name: 'Wolframalpha.com', 
          url: 'https://www.wolframalpha.com', 
          emoji: '🧠', 
          tooltip: 'Computational engine that answers questions and solves mathematical problems.' 
        },
      ],
    },
    {
      category: 'Image & Media Tools',
      emoji: '📸',
      description: 'High-quality image and video tools for editing and creative projects.',
      links: [
        { 
          name: 'Cobalt.tools', 
          url: 'https://cobalt.tools', 
          emoji: '🛠️', 
          tooltip: 'A suite of creative tools for editing and designing digital assets.' 
        },
        { 
          name: 'Unsplash.com', 
          url: 'https://unsplash.com', 
          emoji: '🌄', 
          tooltip: 'A vast library of free, high-resolution photos for your projects.' 
        },
        { 
          name: 'Cleanup.pictures', 
          url: 'https://cleanup.pictures/', 
          emoji: '🧼', 
          tooltip: 'The cleanup.pictures help to remove unwanted objects, people, or defects.' 
        },
        { 
          name: 'Videos.pexels.com', 
          url: 'https://videos.pexels.com', 
          emoji: '🎥', 
          tooltip: 'Stock videos available for free use in your creative projects.' 
        },
        { 
          name: 'Reverse.photos', 
          url: 'https://reverse.photos', 
          emoji: '🔍', 
          tooltip: 'Tool for performing reverse image searches to find source information.' 
        },
        { 
          name: 'Invideo.io', 
          url: 'https://www.invideo.io', 
          emoji: '🎬', 
          tooltip: 'Online video editor with templates to create professional videos quickly.' 
        },
      ],
    },
    {
      category: 'File & Document Tools',
      emoji: '📂',
      description: 'Manage, edit and sign documents with these free file tools.',
      links: [
        { 
          name: 'Zohosign.com', 
          url: 'https://www.zohosign.com', 
          emoji: '✍️', 
          tooltip: 'E-signature solution for secure document signing and management.' 
        },
        { 
          name: 'Jotti.org', 
          url: 'https://www.jotti.org', 
          emoji: '🗂️', 
          tooltip: 'Online virus scanning tool to check files for malware.' 
        },
        { 
          name: 'Iconfinder.com', 
          url: 'https://www.iconfinder.com', 
          emoji: '🎨', 
          tooltip: 'Search and download high-quality icons for your projects.' 
        },
        { 
          name: 'Copychar.cc', 
          url: 'https://copychar.cc', 
          emoji: '🔣', 
          tooltip: 'Tool to easily copy special characters and symbols.' 
        },
        { 
          name: 'CV Builder', 
          url: 'https://builder.zety.com', 
          emoji: '📑', 
          tooltip: 'Online resume builder to create professional CVs with ease.' 
        },
      ],
    },
    {
      category: 'General Tools & Utilities',
      emoji: '🛠️',
      description: 'Everyday utilities and general tools that simplify online tasks.',
      links: [
        { 
          name: 'Tinywow', 
          url: 'https://tinywow.com/', 
          emoji: '🔥🥇', 
          tooltip: 'TinyWow is 100% free to use (even OCR), with no pesky registration required.' 
        },
        { 
          name: 'Seo Studio Tools', 
          url: 'https://seostudiotools.com/', 
          emoji: '🔥🥈', 
          tooltip: 'Discover innovative solutions for keyword analysis, content optimization, and competitor research.' 
        },
        { 
          name: 'Archive.is', 
          url: 'https://archive.is', 
          emoji: '📚', 
          tooltip: 'Web archiving service to capture snapshots of websites.' 
        },
        { 
          name: 'Slides.com', 
          url: 'https://www.slides.com', 
          emoji: '📊', 
          tooltip: 'Online tool to create, present, and share presentations.' 
        },
        { 
          name: 'Earth.google.com', 
          url: 'https://earth.google.com', 
          emoji: '🌍', 
          tooltip: 'Explore satellite imagery and 3D maps of our planet.' 
        },
        { 
          name: 'Semrush.com', 
          url: 'https://www.semrush.com', 
          emoji: '🔎', 
          tooltip: 'Digital marketing suite for SEO, PPC, and competitive research.' 
        },
      ],
    },
    {
      category: 'AI Tools',
      emoji: '🤖',
      description: 'Explore free AI-powered tools and demos to enhance creativity and productivity.',
      links: [
        { 
          name: 'HuggingFace.co', 
          url: 'https://huggingface.co', 
          emoji: '🤗', 
          tooltip: 'Platform hosting a vast collection of open-source machine learning models.' 
        },
        { 
          name: 'ChatGPT', 
          url: 'https://chat.openai.com', 
          emoji: '💬', 
          tooltip: 'Conversational AI by OpenAI that assists with a wide range of queries.' 
        },
        { 
          name: 'DeepAI.org', 
          url: 'https://deepai.org', 
          emoji: '🧬', 
          tooltip: 'Offers various AI tools and APIs for creative and analytical projects.' 
        },
        { 
          name: 'Google AI Experiments', 
          url: 'https://experiments.withgoogle.com/collection/ai', 
          emoji: '🔬', 
          tooltip: 'Showcases innovative AI experiments and interactive demos by Google.' 
        },
        { 
          name: 'ThisPersonDoesNotExist', 
          url: 'https://thispersondoesnotexist.com', 
          emoji: '👤', 
          tooltip: 'Generates hyper-realistic human faces using AI.' 
        },
        { 
          name: 'Pdf to brainrot', 
          url: 'https://pdftobrainrot.org/', 
          emoji: '🧠', 
          tooltip: ' Transform any PDF into a "brainrot" content.' 
        },
      ],
    },
    {
      category: 'Learning & Education',
      emoji: '📚',
      description: 'Free educational resources and online courses to expand your knowledge.',
      links: [
        { 
          name: 'KhanAcademy.org', 
          url: 'https://www.khanacademy.org', 
          emoji: '📖', 
          tooltip: 'Free online lessons covering a wide range of subjects for all ages.' 
        },
        { 
          name: 'MIT OpenCourseWare', 
          url: 'https://ocw.mit.edu', 
          emoji: '🎓', 
          tooltip: 'Access free course materials from MIT’s vast collection of classes.' 
        },
        { 
          name: 'Explorer Globe Engineer', 
          url: 'https://explorer.globe.engineer/', 
          emoji: '🖼️🥇', 
          tooltip: 'In the website the system creates a table of contents on the topic, and then visualizes each section and concept with an image or diagram so you can quickly find what you need.' 
        },
        { 
          name: 'Coursera (Audit mode)', 
          url: 'https://www.coursera.org', 
          emoji: '🎓', 
          tooltip: 'Learn from top universities with free access to course content in audit mode.' 
        },
        { 
          name: 'edX', 
          url: 'https://www.edx.org', 
          emoji: '🎓', 
          tooltip: 'Online courses from leading institutions, with many free options available.' 
        },
        { 
          name: 'Zty', 
          url: 'https://zty.pe', 
          emoji: '🎮', 
          tooltip: 'Interactive platform blending gaming elements with learning experiences.' 
        },
        { 
          name: 'Mindluster', 
          url: 'https://mindluster.com', 
          emoji: '📚', 
          tooltip: 'A resource offering innovative educational content and learning tools.' 
        },
        { 
          name: 'Codédex', 
          url: 'https://www.codedex.io', 
          emoji: '🪙', 
          tooltip: 'A platform for coding challenges and developer learning resources.' 
        },
      ],
    },
  ];
  

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Particles komponens a háttérben */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
        <Particles />
      </div>

      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
      
      <Box sx={{ maxWidth: '100%', margin: 'auto', padding: 3, textAlign: 'center', mt: '64px', }}>
        <Fade in={true} timeout={1000}>
          <Typography variant="h1" sx={{ fontSize: '2.5rem', marginBottom: 2 }}>
            Explore Free Websites 🌐
          </Typography>
        </Fade>
        <Fade in={true} timeout={1500}>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: '#8a8888', marginBottom: 4 }}>
            Discover various free tools and resources available online. Click on the links below to explore! 🔗
          </Typography>
        </Fade>

        {/* Flexbox elrendezés a Box-okhoz */}
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', paddingTop: 4 }}>
          {topics.map((topic) => (
            <TopicBox key={topic.category} topic={topic} />
          ))}
        </Box>
      </Box>
    </div>
  );
};

export default FreeWebSites;
